import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palette, Check, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { THEME_PRESETS, DEFAULT_THEME, applyTheme } from "@/lib/themes";

const COLOR_FIELDS = [
  { key: "sidebar_bg", label: "Sfondo menu" },
  { key: "sidebar_text", label: "Testo voci" },
  { key: "sidebar_hover_bg", label: "Hover voci" },
  { key: "sidebar_active_bg", label: "Voce attiva" },
  { key: "sidebar_active_text", label: "Testo voce attiva" },
  { key: "brand_color", label: "Colore brand (accenti)" },
];

export default function ThemeSettings() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState(() => ({ ...DEFAULT_THEME, ...(user?.theme || {}) }));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Applica tema live-preview ogni volta che cambia lo stato
  useEffect(() => {
    applyTheme(current);
  }, [current]);

  // Cleanup: se l'utente esce senza salvare, ripristina il tema salvato del profilo
  useEffect(() => {
    return () => {
      applyTheme(user?.theme || DEFAULT_THEME);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickPreset = (preset) => {
    setCurrent({ ...preset, preset: preset.id });
    setDirty(true);
  };

  const setField = (k, v) => {
    setCurrent((c) => ({ ...c, [k]: v, preset: "custom" }));
    setDirty(true);
  };

  const resetToDefault = () => {
    setCurrent({ ...DEFAULT_THEME, preset: DEFAULT_THEME.id });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        preset: current.preset || "custom",
        sidebar_bg: current.sidebar_bg,
        sidebar_text: current.sidebar_text,
        sidebar_hover_bg: current.sidebar_hover_bg,
        sidebar_active_bg: current.sidebar_active_bg,
        sidebar_active_text: current.sidebar_active_text,
        brand_color: current.brand_color,
      };
      await api.put("/auth/me/theme", payload);
      if (typeof refresh === "function") await refresh();
      setDirty(false);
      toast.success("Tema salvato! ✨");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const activePresetId = current.preset || DEFAULT_THEME.id;

  return (
    <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="theme-settings">
      <div className="mb-4 flex items-center gap-2">
        <Palette className="h-5 w-5 text-[color:var(--brand-color)]" />
        <h2 className="text-lg font-bold text-slate-900">Aspetto (tema personale)</h2>
      </div>
      <p className="mb-5 text-sm text-slate-500">
        Personalizza i colori del menu e degli accenti dell'app. La scelta vale solo per il tuo account.
      </p>

      {/* Preset gallery */}
      <div className="mb-6">
        <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Temi preimpostati</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {THEME_PRESETS.map((p) => {
            const isActive = activePresetId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => pickPreset(p)}
                data-testid={`theme-preset-${p.id}`}
                className={`group relative overflow-hidden rounded-sm border-2 p-2 text-left transition-all hover:scale-[1.02] ${isActive ? "border-emerald-500 ring-2 ring-emerald-200" : "border-slate-200"}`}
              >
                {/* preview mini-sidebar */}
                <div className="mb-2 h-14 w-full rounded-sm" style={{ backgroundColor: p.sidebar_bg }}>
                  <div className="flex items-center gap-1.5 p-1.5">
                    <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: p.brand_color }} />
                    <div className="h-1.5 flex-1 rounded-sm" style={{ backgroundColor: p.sidebar_text, opacity: 0.5 }} />
                  </div>
                  <div className="mx-1.5 h-2 rounded-sm" style={{ backgroundColor: p.sidebar_active_bg }} />
                  <div className="mx-1.5 mt-1 h-1.5 rounded-sm" style={{ backgroundColor: p.sidebar_text, opacity: 0.3 }} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-bold text-slate-900">{p.emoji} {p.name}</div>
                    <div className="truncate text-[10px] text-slate-500">{p.description}</div>
                  </div>
                  {isActive && <Check className="h-4 w-4 flex-shrink-0 text-emerald-600" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom color pickers */}
      <details className="mb-6 rounded-sm border border-slate-200 bg-slate-50 p-3">
        <summary className="cursor-pointer text-xs font-bold uppercase tracking-widest text-slate-600">
          Personalizzazione avanzata (colori custom)
        </summary>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {COLOR_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-2 rounded-sm bg-white p-2">
              <label className="text-xs font-semibold text-slate-700">{f.label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={current[f.key] || "#000000"}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="h-8 w-10 cursor-pointer rounded-sm border border-slate-200"
                  data-testid={`theme-color-${f.key}`}
                />
                <span className="font-mono text-[11px] uppercase text-slate-500">{current[f.key]}</span>
              </div>
            </div>
          ))}
        </div>
      </details>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
          data-testid="theme-save"
        >
          <Save className="mr-2 h-4 w-4" /> {saving ? "Salvataggio…" : "Salva tema"}
        </Button>
        <Button
          onClick={resetToDefault}
          variant="outline"
          className="rounded-sm"
          data-testid="theme-reset"
        >
          <RotateCcw className="mr-2 h-4 w-4" /> Ripristina default
        </Button>
        {dirty && (
          <span className="text-xs text-amber-700">
            💡 Anteprima live attiva — clicca Salva per mantenere la scelta
          </span>
        )}
      </div>
    </div>
  );
}
